import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { SubscriptionControlService } from '../../Modules/Subscriptions/Services/subscription-control.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import {
  CANCEL_APPLIED_MESSAGE,
  CHANGE_APPLIED_MESSAGE,
  OVERRIDE_APPLIED_MESSAGE,
  OVERRIDE_REVERTED_MESSAGE,
  PAUSE_APPLIED_MESSAGE,
  RESUME_APPLIED_MESSAGE,
  ROLE_SUBSCRIPTION_CONTROL,
  TRIAL_APPLIED_MESSAGE,
} from '../../Consts/subscription-control.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { OrganizationSubscription } from '../../Models/backoffice/organization-subscription.model';
import { PlanSummary } from '../../Models/backoffice/plan-summary.model';
import { SubscriptionOverride } from '../../Models/backoffice/subscription-override.model';
import { SubscriptionPreview } from '../../Models/backoffice/subscription-preview.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationShowResponse } from '../../Interfaces/api/backoffice/organization.interface';
import { PlanListResponse } from '../../Interfaces/api/backoffice/plan.interface';
import {
  SubscriptionCancelPayload,
  SubscriptionItemResponse,
  SubscriptionOverrideListResponse,
  SubscriptionOverridePayload,
  SubscriptionShowResponse,
  SubscriptionTrialPayload,
} from '../../Interfaces/api/backoffice/subscription.interface';

@Injectable()
export class SubscriptionControlFacade {
  private readonly _controlService = inject(SubscriptionControlService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _organizationId = signal<string>('');
  private readonly _targetPlanId = signal<string>('');
  private readonly _targetOfferType = signal<string>('');
  private readonly _selectedItemId = signal<string>('');
  private readonly _preview = signal<SubscriptionPreview | null>(null);
  private readonly _isMutating = signal<boolean>(false);
  private readonly _isPreviewing = signal<boolean>(false);

  private readonly _subscription = httpResource<LaravelDefaultResponse<SubscriptionShowResponse>>(
    () =>
      this._organizationId()
        ? this._controlService.showRequest(this._organizationId())
        : undefined,
  );

  private readonly _plans = httpResource<LaravelDefaultResponse<PlanListResponse>>(() =>
    this._organizationId() ? this._controlService.plansRequest() : undefined,
  );

  private readonly _organization = httpResource<LaravelDefaultResponse<OrganizationShowResponse>>(
    () =>
      this._organizationId()
        ? this._organizationService.showRequest(this._organizationId())
        : undefined,
  );

  private readonly _overrides = httpResource<
    LaravelDefaultResponse<SubscriptionOverrideListResponse>
  >(() =>
    this._organizationId() && this.selectedItemId()
      ? this._controlService.overridesRequest(this._organizationId(), this.selectedItemId())
      : undefined,
  );

  readonly subscription = computed<OrganizationSubscription | null>(() =>
    this._controlService.toSubscription(this._resourceState.valueOf(this._subscription)),
  );

  readonly organizationId = computed<string>(() => this._organizationId());

  readonly organizationName = computed<string>(
    () => this._organizationService.toDetail(this._resourceState.valueOf(this._organization))?.displayName ?? '',
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._subscription.status()),
  );

  readonly plans = computed<PlanSummary[]>(() =>
    this._controlService.toPlans(this._resourceState.valueOf(this._plans)).filter((plan) => plan.is_active),
  );

  readonly plansState = computed<MetricState>(() =>
    this._resourceState.metricState(this._plans.status()),
  );

  readonly items = computed<SubscriptionItemResponse[]>(() => this.subscription()?.items ?? []);

  readonly selectedItemId = computed<string>(
    () => this._selectedItemId() || this.items()[0]?.id || '',
  );

  readonly selectedItem = computed<SubscriptionItemResponse | null>(
    () => this.items().find((item) => item.id === this.selectedItemId()) ?? null,
  );

  readonly overrides = computed<SubscriptionOverride[]>(() =>
    this._controlService.toOverrides(this._resourceState.valueOf(this._overrides)),
  );

  readonly overridesState = computed<MetricState>(() =>
    this._resourceState.metricState(this._overrides.status()),
  );

  readonly targetPlanId = computed<string>(() => this._targetPlanId());

  readonly targetOfferType = computed<string>(
    () => this._targetOfferType() || this.subscription()?.seat_offer_type || '',
  );

  readonly preview = computed<SubscriptionPreview | null>(() => this._preview());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly isPreviewing = computed<boolean>(() => this._isPreviewing());

  readonly isOwned = computed<boolean>(() => this.subscription()?.stripe_customer_owned ?? false);

  readonly hasRole = computed<boolean>(() =>
    this._operatorFacade.atLeast(ROLE_SUBSCRIPTION_CONTROL),
  );

  readonly canWrite = computed<boolean>(
    () => this.hasRole() && this.isOwned() && !this._isMutating(),
  );

  readonly isCommitmentLocked = computed<boolean>(
    () => this.subscription()?.commitment.is_cancellable === false,
  );

  readonly isTrialing = computed<boolean>(() => this.subscription()?.isTrialing ?? false);

  readonly isPreviewFresh = computed<boolean>(() => {
    const preview = this._preview();

    return preview !== null && preview.matches(this.targetPlanId(), this.targetOfferType());
  });

  readonly isPreviewStale = computed<boolean>(
    () => this._preview() !== null && !this.isPreviewFresh(),
  );

  readonly canPreview = computed<boolean>(() => this.canWrite() && this.targetPlanId() !== '');

  readonly canApplyChange = computed<boolean>(() => this.canWrite() && this.isPreviewFresh());

  load(organizationId: string): void {
    this._organizationId.set(organizationId);
  }

  selectPlan(planId: string): void {
    this._targetPlanId.set(planId);
  }

  selectOfferType(offerType: string): void {
    this._targetOfferType.set(offerType);
  }

  selectItem(itemId: string): void {
    this._selectedItemId.set(itemId);
  }

  reload(): void {
    this._subscription.reload();
    this._overrides.reload();
  }

  requestPreview(): void {
    if (!this.canPreview()) {
      return;
    }

    this._isPreviewing.set(true);

    this._controlService
      .preview(this._organizationId(), {
        plan_id: this.targetPlanId(),
        offer_type: this.targetOfferType() || null,
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (preview) => this._acceptPreview(preview),
        error: (error) => this._failPreview(error),
      });
  }

  applyChange(overrideCommitmentLock: boolean, overrideReason: string | null): void {
    if (!this.canApplyChange()) {
      return;
    }

    this._isMutating.set(true);

    this._controlService
      .update(this._organizationId(), {
        plan_id: this.targetPlanId(),
        offer_type: this.targetOfferType() || null,
        override_commitment_lock: overrideCommitmentLock,
        override_reason: overrideReason ?? undefined,
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(CHANGE_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  pause(reason: string): void {
    if (!this.canWrite()) {
      return;
    }

    this._isMutating.set(true);

    this._controlService
      .pause(this._organizationId(), reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(PAUSE_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  resume(reason: string): void {
    if (!this.canWrite()) {
      return;
    }

    this._isMutating.set(true);

    this._controlService
      .resume(this._organizationId(), reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(RESUME_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  cancel(payload: SubscriptionCancelPayload): void {
    if (!this.canWrite()) {
      return;
    }

    this._isMutating.set(true);

    this._controlService
      .cancel(this._organizationId(), payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(CANCEL_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  extendTrial(payload: SubscriptionTrialPayload): void {
    if (!this.canWrite()) {
      return;
    }

    this._isMutating.set(true);

    this._controlService
      .extendTrial(this._organizationId(), payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(TRIAL_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  applyOverride(payload: SubscriptionOverridePayload): void {
    const itemId = this.selectedItemId();

    if (!this.canWrite() || !itemId) {
      return;
    }

    this._isMutating.set(true);

    this._controlService
      .applyOverride(this._organizationId(), itemId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(OVERRIDE_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  revertOverride(overrideId: string, reason: string): void {
    const itemId = this.selectedItemId();

    if (!this.canWrite() || !itemId) {
      return;
    }

    this._isMutating.set(true);

    this._controlService
      .revertOverride(this._organizationId(), itemId, overrideId, reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(OVERRIDE_REVERTED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  private _acceptPreview(preview: SubscriptionPreview): void {
    this._isPreviewing.set(false);
    this._preview.set(preview);
  }

  private _failPreview(error: unknown): void {
    this._isPreviewing.set(false);
    this._preview.set(null);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }

  private _afterWrite(message: string): void {
    this._isMutating.set(false);
    this._preview.set(null);
    this._subscription.reload();
    this._overrides.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
